import { useContext, useState } from 'react'
import { LootContext, PersonalLootContext } from '../context'
import {
  difficulties,
  difficultiesILVLDeficit,
} from 'app/frontend/types/custom/Difficulty'
import { flatten, maxBy } from 'lodash'
import { useTeam } from 'app/frontend/base/config/context/team'
import RelativeTime from 'dayjs/plugin/relativeTime'
import dayjs from 'dayjs'
import React from 'react'
import { Clock } from 'lucide-react'
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
} from '@/components/ui/dialog'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { deleteWishlistsForTeam } from 'app/frontend/base/api/character'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { displayErrorToast } from 'app/frontend/base/helpers'
import DestructiveField from 'app/frontend/base/components/form-fields/DestructiveField'
import { useAuth } from 'app/frontend/base/config/context/auth'
dayjs.extend(RelativeTime)

type Props = {
  wishlist?: wowaudit.CharacterWishlist
}

const PersonalAgeModal = ({ wishlist }: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { team } = useTeam()
  const {
    data: { user },
  } = usePageInfo()
  const queryClient = useQueryClient()
  const { accessLevel } = useAuth()
  const { droptimizerConfigurations, selectedSeason } = useContext(LootContext)
  const { characterReference } = useContext(PersonalLootContext)
  if (!wishlist) return null

  const { mutate: deleteWishlists, isPending } = useMutation({
    mutationFn: () =>
      deleteWishlistsForTeam(characterReference.id, team.id, selectedSeason.id),
    onSuccess: () => {
      toast.success('Wishlist information has been reset.')
      queryClient.invalidateQueries({
        queryKey: [
          'characterWishlist',
          {
            teamId: team.id,
            characterId: characterReference.id,
            seasonId: selectedSeason.id,
          },
        ],
      })
    },
    onError: (error) => displayErrorToast(error, t),
  })

  return (
    <>
      <Clock
        className='cursor-pointer text-muted-foreground hover:text-foreground transition-colors'
        onClick={() => setOpen(true)}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Time since last update</DialogTitle>
          </DialogHeader>

          {droptimizerConfigurations.map((config) => (
            <div key={config.id} className='mb-4'>
              {droptimizerConfigurations.length > 1 && <h4>{config.name}</h4>}
              <div className='flex gap-2'>
                {difficulties.map((diff, i) => {
                  if (team.settings.wishlistDifficulties.value.includes(diff)) {
                    return (
                      <div key={diff} className='grow'>
                        <h5>{Object.keys(difficultiesILVLDeficit)[i]}</h5>
                        <div className='flex flex-col gap-1'>
                          {wishlist.specs.map((spec) => {
                            const relevantWishes = flatten(
                              wishlist.items.map((item) =>
                                item.wishes.filter(
                                  (wish) =>
                                    wish.difficulty === i &&
                                    wish.specialization === spec.id &&
                                    wish.configuration_id === config.id,
                                ),
                              ),
                            )

                            const reportUrl = relevantWishes.filter(
                              (wish) => wish.report_url,
                            )[0]?.report_url

                            const age = maxBy(
                              relevantWishes,
                              (wish) => new Date(wish.updated_at),
                            )?.updated_at

                            const difference = dayjs().diff(dayjs(age), 'day')

                            return (
                              <div
                                key={spec.id}
                                className='flex items-center gap-2'
                              >
                                <BlizzardIcon
                                  iconName={spec.icon}
                                  title={spec.name}
                                  size='large'
                                />
                                {React.createElement(
                                  reportUrl ? `a` : `span`,
                                  {
                                    className: `${
                                      reportUrl ? 'underline' : ''
                                    } ${
                                      age
                                        ? difference > 21
                                          ? 'text-destructive'
                                          : difference > 7
                                          ? 'text-yellow-500'
                                          : 'text-primary'
                                        : 'text-muted-foreground'
                                    }`,
                                    href: reportUrl,
                                    target: '_blank',
                                    rel: 'noreferrer',
                                  },
                                  age ? dayjs(age).fromNow() : 'never',
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  }
                })}
              </div>
            </div>
          ))}

          {(user && characterReference.userId === user?.id) ||
          accessLevel === 'admin' ? (
            <DestructiveField
              label='Reset wishlist'
              buttonText='Reset'
              description={`Reset all of ${characterReference.name}'s wishlist data for this team.`}
              confirmation="Are you sure you want to reset all of this character's wishlist information for this team? This cannot be undone."
              onDelete={() => deleteWishlists()}
              loading={isPending}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PersonalAgeModal
